import { Separator } from '@components/ui/separator'
import { Skeleton } from '@components/ui/skeleton'
import { SectionHeading } from './section-heading'

/**
 * Placeholder for the Font Family → Decoration controls while the used-font-families catalog is
 * loading (or the connection is slow). Chrome (section/field labels) matches the real content
 * exactly; only the data-bound control boxes swap for `Skeleton`, sized to each real control's
 * box so nothing shifts layout when the catalog resolves.
 */
export function TypePanelLoadingSkeleton() {
  const presetRow = (count: number, chipClassName: string) => (
    <div className="flex gap-1">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className={chipClassName} />
      ))}
    </div>
  )
  const sliderRow = () => (
    <div className="flex items-center gap-[1.125rem] mt-2">
      <Skeleton className="h-4 flex-1 min-w-0 max-w-[8.125rem] rounded-full" />
      <Skeleton className="h-9 w-[88px] rounded-md flex-shrink-0" />
    </div>
  )

  return (
    <div aria-hidden="true">
      {/* Font Family Section */}
      <div className="space-y-3">
        <SectionHeading>Font Family</SectionHeading>
        <div className="mt-2 space-y-2">
          <label
            style={{ fontSize: '14px' }}
            className="text-xs font-medium text-foreground leading-normal "
          >
            Font
          </label>
          <div className="flex gap-2 flex-wrap">
            <Skeleton className="h-7 w-16 rounded-md" />
            <Skeleton className="h-7 w-20 rounded-md" />
            <Skeleton className="h-7 w-14 rounded-md" />
          </div>
          <Skeleton className="h-8 w-full" />
        </div>
      </div>

      {/* Font variant (Regular / Bold / Italic …) */}
      <div className="space-y-3 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Style</label>
        <Skeleton className="h-8 w-full mt-2" />
      </div>

      <Separator className="my-4" />

      {/* Size Section */}
      <div className="space-y-3">
        <SectionHeading>Size</SectionHeading>
        <label className="text-xs font-medium text-foreground leading-none block mt-2">
          Font Size
        </label>
        {presetRow(5, 'h-7 w-10 rounded-md')}
        {sliderRow()}
      </div>

      <Separator className="my-4" />

      {/* Spacing Section */}
      <div>
        <SectionHeading>Spacing</SectionHeading>
      </div>

      {/* Leading Section */}
      <div className="flex flex-col gap-2 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Leading</label>
        {presetRow(3, 'h-8 w-14 rounded-md')}
        {sliderRow()}
      </div>

      {/* Kerning Section */}
      <div className="flex flex-col gap-2 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Kerning</label>
        {presetRow(3, 'h-8 w-14 rounded-md')}
        {sliderRow()}
      </div>

      {/* Tracking Section */}
      <div className="flex flex-col gap-2 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Tracking</label>
        {presetRow(3, 'h-8 w-14 rounded-md')}
        {sliderRow()}
      </div>

      <Separator className="my-4" />

      {/* Alignment and Formatting Section */}
      <div>
        <SectionHeading>Alignment and Formatting</SectionHeading>
      </div>

      {/* Align Section */}
      <div className="space-y-3 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal mt-2">Align</label>
        <div className="flex gap-1 mt-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="size-9 rounded-md" />
          ))}
        </div>
      </div>

      {/* Decoration Section */}
      <div className="space-y-3 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Decoration</label>
        <div className="flex gap-1 mt-2">
          {Array.from({ length: 2 }, (_, i) => (
            <Skeleton key={i} className="size-9 rounded-md" />
          ))}
        </div>
      </div>

      {/* Drop Cap Section */}
      <div className="space-y-3 mt-4">
        <div className="flex items-center gap-2">
          <Skeleton className="size-4 rounded-sm" />
          <label className="text-sm font-medium text-foreground leading-normal">Drop Cap</label>
        </div>
        <div className="flex gap-1 mt-2">
          <Skeleton className="size-9 rounded-md" />
          <Skeleton className="size-9 rounded-md" />
        </div>
      </div>

      {/* Style Presets Section */}
      <div className="mt-4">
        <div className="flex gap-2">
          <Skeleton className="h-7 w-14 rounded-md" />
          <Skeleton className="h-7 w-16 rounded-md" />
          <Skeleton className="h-7 w-14 rounded-md" />
        </div>
      </div>

      {/* Lines Section */}
      <div className="space-y-3 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Lines</label>
        {sliderRow()}
      </div>

      {/* Offset Section */}
      <div className="space-y-3 mt-4">
        <label className="text-sm font-medium text-foreground leading-normal">Offset</label>
        {sliderRow()}
      </div>

      <Separator className="my-4" />

      {/* Text Styles Section */}
      <div>
        <SectionHeading>Text Styles</SectionHeading>
      </div>

      {/* Heading Large Section */}
      <Skeleton className="h-[100px] w-full rounded-md mt-2" />
    </div>
  )
}
